import React, { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, InputGroup, Input } from 'reactstrap';
import { PageTitle, TableButtons, CreateButton, Table, Ability } from '../../../components';
import { Filters, Utils } from '../../../services/';
import { Link, useHistory } from "react-router-dom"
import _ from 'lodash';
import 'react-table-6/react-table.css'
import { loadUsers, loadInactiveUsers, spoof, loadTktInstances, setMessagesUsersIds, applyUsersAbilities } from '../../../redux';

import './UsersList.css';
import user_avatar from '../../../assets/images/user_avatar.jpg';

const UsersList = ({ }) => {
    const history  = useHistory();
    const dispatch = useDispatch();
    const { t }    = useTranslation();

    const default_lang = Utils.default_lang();

    const [selection, setSelection]   = useState([]);
    const [showActive, setShowActive] = useState(true);
    const [templateId, setTemplateId] = useState(null);

    const {
        users,
        inactive_users,
        applyBatchAbilitiesMessage
    }                                 = useSelector(state => state.users);
    const authUser                    = useSelector(state => state.auth.user);
    const { tktInstances }            = useSelector(state => state.tktInstances);
    const { isMobile, isTablet }      = useSelector(state => state.ui);
    const users_to_show               = showActive ? users : inactive_users;

    const toggle_active = (active) => {
        setShowActive(active);
    }

    const spoofUser = user_id => {
        dispatch(spoof(user_id));
    };

    const isSelected = React.useCallback(id => selection.includes(id), [ selection ]);
    const onChangedCheckbox = id => {
        if (isSelected(id))
            setSelection(selection.filter(i => i !== id));
        else
            setSelection([...selection, id]);
    };
    const selectAll = React.useCallback(all => {
        setSelection(all ?
            users_to_show?.map(u => u.id) :
            []
        );
    }, [users_to_show]);
    const allSelected = React.useMemo(() => {
        if (!users_to_show?.length || !selection?.length)
            return false;

        if (users_to_show.length !== selection?.length)
            return false;

        return users_to_show.reduce(
            (allSelected, user) => allSelected && selection.includes(user.id),
            true
        );
    }, [users_to_show, selection]);
    const sendBatchEmail = () => {
        dispatch(setMessagesUsersIds(selection));
        history.push('/messages');
    }

    const batchApplyAbilities = () => {
        dispatch(applyUsersAbilities(templateId, selection));
    }

    useEffect(() => {
        dispatch(loadUsers());
        dispatch(loadInactiveUsers());
        authUser.abilities.superAdmin && dispatch(loadTktInstances());
    }, [])

    const abilitiesOptions = authUser?.tkt_instance?.abilities_templates?.map((template, index) => {
        return [
            <option
                key={index}
                value={index}
            >
                {template.name}
            </option>
        ];
    });

    let activities = [];
    if (users_to_show) {
        activities = _.uniqBy(_.flatten(users_to_show.map((u) => u.activities)), 'id');
        activities = _.sortBy(activities, (a) => a.name[default_lang]);
    }

    let columns = [{
        Header: cell => (
            <input
                className="checkbox"
                type="checkbox"
                checked={!!allSelected}
                onChange={() => selectAll(!allSelected)}
                value={cell.value}
            />
        ),
        filterable: false,
        sortable: false,
        accessor: 'id',
        maxWidth: 90,
        className: "checkbox-cell",
        Cell: cell => (
            <input
                className="checkbox"
                type="checkbox"
                checked={isSelected(cell.value)}
                onChange={() => onChangedCheckbox(cell.value)} value={cell.value}
            />
        )
    }, {
        Header: t('users.fullname'),
        accessor: "rfullname",
        filterMethod: Filters.textFilterMethod("rfullname"),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => row.value && (
            <div className={`user_cell ${isMobile || isTablet ? 'd-flex column items-start' : ''}`}>
                <div className="img_wrapper">
                    <img src={row.original.avatar || user_avatar} />
                </div>
                <div>
                    <Link to={`/users/edit/${row.original.id}`}>
                        <span className="fullname">{row.original.fullname}</span>
                    </Link>
                </div>
            </div>
        )
    }, {
        Header: t('users.activities'),
        accessor: "activities",
        filterMethod: Filters.usersActivitiesFilterMethod,
        Filter: Filters.activitiesFilter(activities),
        Cell: row => (
            <div>
                {row.original.activities &&
                    <h5>
                        {row.original.activities.map((a) => <span key={a.id} className="badge badge-light mr-2"> {a.name[default_lang]}</span>)}
                    </h5>
                }
            </div>
        )
    }, {
        Header: t('users.email'),
        accessor: "email",
        filterMethod: Filters.textFilterMethod("email"),
        Filter: Filters.textFilter,
        filterAll: true,
    }];

    if (authUser?.isSuperAdmin() && tktInstances) {
        columns = columns.concat([{
            Header: t('common.tktInstance', {count:1}),
            accessor: "tkt_instance_id",
            filterMethod: Filters.textFilterMethod("tkt_instance_id"),
            Filter: Filters.tktInstancesFilter(tktInstances),
            filterAll: true,
            Cell: row => row.value && tktInstances.length > 0 && (
                <span>
                    {tktInstances.find((t) => t.id === row.value) && tktInstances.find((t) => t.id === row.value).name}
                </span>
            )
        }]);
    }

    columns = columns.concat([{
        Header: t('common.actions'),
        accessor: "id",
        maxWidth: authUser?.can('user:spoof') ? 130 : 110,
        filterable: false,
        Cell: row => (
            <>
                <TableButtons
                    id={row.value}
                    actions={["edit", "delete", "show", "spoof"]}
                    module="users"
                    owner={row.original.created_by}
                />
                <Ability owner={row.original.created_by} can={'user:spoof'}>
                    <Button className="ml-1" onClick={() => spoofUser(row.value)} color="warning" title={t("users.spoof")} size="sm">
                        <i className="fa fa-user-secret"></i>
                    </Button>
                </Ability>
            </>
        )
    }]);

    const actions =
        <div>
            {(authUser?.isSuperAdmin() || authUser?.can('edit_templates', 'users')) &&
                <Button tag={Link} color="primary" to={`/tktInstances/edit/${authUser.tkt_instance.id}`} size="sm" className="mr-2">
                    <i className="fa fa-edit"></i>&nbsp;
                    {t('abilities.abilities_template')}
                </Button>
            }
            <ButtonGroup>
                <Button color={showActive ? 'info' : 'secondary'} onClick={(e) => toggle_active(true)} size="sm">{t('users.active')}</Button>
                <Button color={!showActive ? 'info' : 'secondary'} onClick={(e) => toggle_active(false)} size="sm">{t('users.inactive')}</Button>
            </ButtonGroup>&nbsp;
            <CreateButton
                module="users"
                text="users.add_a_user"
            />
        </div>;

    return (
        <div className="UsersList">
            <PageTitle icon="users" title={t('users.user', {count: 42})} actions={actions} />
            {selection?.length > 0 && (
                <div className="border rounded d-flex gap-2 p-1 mb-1 justify-start">
                    {abilitiesOptions?.length > 0 && authUser.can('edit_abilities', 'users') &&
                        <InputGroup style={{ maxWidth: '300px' }}>
                            <Input
                                type="select"
                                onChange={e => setTemplateId(e.target?.value)}
                                placeholder={t("abilities.chose_abilities_placeholder")}
                            >
                                <option key="none" value="">---</option>
                                {abilitiesOptions}
                            </Input>
                            <Button
                                color="success"
                                onClick={() => batchApplyAbilities()}
                            >
                                {t("abilities.apply_template")}
                            </Button>
                        </InputGroup>
                    }
                    {authUser.can('create', 'messages') && (
                        <Button onClick={ sendBatchEmail } color="primary">
                            <i className="fa fa-envelope" />&nbsp;
                            { t('tickets.batch.send_email') }
                        </Button>
                    )}
                </div>
            )}
            <Table id="usersTable" data={users_to_show} columns={columns} />
            {applyBatchAbilitiesMessage?.length > 0 && (
                <div
                    className="alert alert-success"
                    dangerouslySetInnerHTML={{__html: applyBatchAbilitiesMessage}}
                />
            )}
        </div>
    );
}

export default UsersList;
